/**
 * Sector operations — Compliance Calendar Service (R39 A9, SO-P66).
 * Manages compliance items, history subscription, query constraints, and evidence storage.
 */

import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
  limit,
  type QueryConstraint,
  type Unsubscribe,
} from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { firestoreDb, firebaseStorage, firebaseAuth } from './firebase'
import { callable } from './callable'
import { getNlsString } from '../nls/nlsRuntime'
import type {
  OpsComplianceItem,
  OpsComplianceHistoryEntry,
  CreateComplianceItemInput,
  UpdateComplianceItemInput,
  RenewComplianceItemInput,
  AttachComplianceEvidenceInput,
  ArchiveComplianceItemInput,
} from '../types/ops'

export const ALLOWED_COMPLIANCE_EVIDENCE_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
] as const

export const MAX_COMPLIANCE_EVIDENCE_BYTES = 10 * 1024 * 1024 // 10 MB

function getSafeExtension(file: File): string {
  if (file.name.includes('.')) {
    const ext = file.name.split('.').pop()?.toLowerCase()
    if (ext && /^[a-z0-9]+$/.test(ext)) {
      return ext
    }
  }
  switch (file.type) {
    case 'application/pdf':
      return 'pdf'
    case 'image/jpeg':
      return 'jpg'
    case 'image/png':
      return 'png'
    case 'image/webp':
      return 'webp'
    case 'image/heic':
      return 'heic'
    default:
      return 'bin'
  }
}

// ---------------------------------------------------------------------------
// Callables
// ---------------------------------------------------------------------------

export async function createComplianceItem(
  input: CreateComplianceItemInput,
): Promise<{ itemId: string; status: string }> {
  return callable<CreateComplianceItemInput, { itemId: string; status: string }>(
    'createComplianceItem',
    input,
  )
}

export async function updateComplianceItem(
  input: UpdateComplianceItemInput,
): Promise<{ itemId: string; status: string; version: number }> {
  return callable<
    UpdateComplianceItemInput,
    { itemId: string; status: string; version: number }
  >('updateComplianceItem', input)
}

export async function renewComplianceItem(
  input: RenewComplianceItemInput,
): Promise<{ itemId: string; status: string; version: number }> {
  return callable<
    RenewComplianceItemInput,
    { itemId: string; status: string; version: number }
  >('renewComplianceItem', input)
}

export async function attachComplianceEvidence(
  input: AttachComplianceEvidenceInput,
): Promise<{ itemId: string; evidenceCount: number; version: number }> {
  return callable<
    AttachComplianceEvidenceInput,
    { itemId: string; evidenceCount: number; version: number }
  >('attachComplianceEvidence', input)
}

export async function archiveComplianceItem(
  input: ArchiveComplianceItemInput,
): Promise<{ itemId: string; status: string }> {
  return callable<ArchiveComplianceItemInput, { itemId: string; status: string }>(
    'archiveComplianceItem',
    input,
  )
}

// ---------------------------------------------------------------------------
// Query Constraints & Subscriptions
// ---------------------------------------------------------------------------

export interface OpsComplianceQueryOptions {
  callerUid?: string
  viewerUid?: string
  isElevated?: boolean
  isRestricted?: boolean
  includeArchived?: boolean
}

/**
 * Pure builder for Firestore query constraints used by subscribeOpsComplianceItems and rules tests.
 * Rules require non-restricted members or (isRestricted & (readAccess == null | contains caller | owner | backup)).
 */
export function buildOpsComplianceQueryConstraints(
  workspaceId: string,
  options: OpsComplianceQueryOptions,
): QueryConstraint[][] {
  const callerUid = options.callerUid?.trim() || options.viewerUid?.trim()
  if (!callerUid) {
    return []
  }

  const queries: QueryConstraint[][] = []
  const archived = options.includeArchived === true

  if (options.isRestricted === true) {
    // 1. Items with no place restriction (readAccess == null)
    queries.push([
      where('workspaceId', '==', workspaceId),
      where('readAccess', '==', null),
      where('archived', '==', archived),
      orderBy('validTo', 'asc'),
      limit(200),
    ])

    // 2. Items where place restriction includes caller
    queries.push([
      where('workspaceId', '==', workspaceId),
      where('readAccess', 'array-contains', callerUid),
      where('archived', '==', archived),
      orderBy('validTo', 'asc'),
      limit(200),
    ])

    // 3. Items owned by caller
    queries.push([
      where('workspaceId', '==', workspaceId),
      where('ownerUid', '==', callerUid),
      where('archived', '==', archived),
      orderBy('validTo', 'asc'),
      limit(200),
    ])

    // 4. Items where caller is backup
    queries.push([
      where('workspaceId', '==', workspaceId),
      where('backupUid', '==', callerUid),
      where('archived', '==', archived),
      orderBy('validTo', 'asc'),
      limit(200),
    ])
  } else {
    // Plain or elevated member: can read all items in workspace
    queries.push([
      where('workspaceId', '==', workspaceId),
      where('archived', '==', archived),
      orderBy('validTo', 'asc'),
      limit(200),
    ])
  }

  return queries
}

export function subscribeOpsComplianceItems(
  workspaceId: string,
  options: OpsComplianceQueryOptions,
  onUpdate: (items: OpsComplianceItem[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const callerUid = options.callerUid?.trim() || options.viewerUid?.trim()
  if (!callerUid) {
    onUpdate([])
    return () => {}
  }

  const queryGroups = buildOpsComplianceQueryConstraints(workspaceId, options)
  if (queryGroups.length === 0) {
    onUpdate([])
    return () => {}
  }

  const coll = collection(firestoreDb, 'ops_compliance_items')
  const streamDocs: OpsComplianceItem[][] = []
  const unsubs: Unsubscribe[] = []

  const mergeAndEmit = () => {
    const map = new Map<string, OpsComplianceItem>()
    for (const group of streamDocs) {
      for (const item of group) {
        map.set(item.id, item)
      }
    }
    const merged = Array.from(map.values())
    merged.sort(
      (a, b) =>
        a.validTo.localeCompare(b.validTo) ||
        a.title.localeCompare(b.title) ||
        a.id.localeCompare(b.id),
    )
    onUpdate(merged)
  }

  queryGroups.forEach((constraints, index) => {
    streamDocs[index] = []
    const q = query(coll, ...constraints)
    const unsub = onSnapshot(
      q,
      (snap) => {
        streamDocs[index] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<OpsComplianceItem, 'id'>),
        }))
        mergeAndEmit()
      },
      (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
    )
    unsubs.push(unsub)
  })

  return () => {
    for (const unsub of unsubs) {
      unsub()
    }
  }
}

export function subscribeOpsComplianceHistory(
  workspaceId: string,
  itemId: string,
  onUpdate: (entries: OpsComplianceHistoryEntry[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  void workspaceId
  if (!itemId) {
    onUpdate([])
    return () => {}
  }
  const coll = collection(firestoreDb, 'ops_compliance_items', itemId, 'history')
  const q = query(coll, orderBy('at', 'asc'))
  return onSnapshot(
    q,
    (snap) => {
      const items: OpsComplianceHistoryEntry[] = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<OpsComplianceHistoryEntry, 'id'>),
      }))
      onUpdate(items)
    },
    (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
  )
}

// ---------------------------------------------------------------------------
// Storage: Evidence Upload & Download
// ---------------------------------------------------------------------------

export interface UploadComplianceEvidenceResult {
  path: string
  downloadUrl: string
}

export async function uploadComplianceEvidence(
  workspaceId: string,
  itemId: string,
  file: File,
): Promise<UploadComplianceEvidenceResult> {
  if (!workspaceId) throw new Error('workspaceId is required.')
  if (!itemId) throw new Error('itemId is required.')

  if (
    !ALLOWED_COMPLIANCE_EVIDENCE_MIME_TYPES.includes(
      file.type as (typeof ALLOWED_COMPLIANCE_EVIDENCE_MIME_TYPES)[number],
    )
  ) {
    throw new Error(getNlsString('NLSOpsComplianceEvidenceInvalidType'))
  }

  if (file.size > MAX_COMPLIANCE_EVIDENCE_BYTES) {
    throw new Error(getNlsString('NLSOpsComplianceEvidenceTooLarge'))
  }

  const uid = firebaseAuth.currentUser?.uid
  if (!uid) {
    throw new Error('Authentication required for evidence upload.')
  }

  const ext = getSafeExtension(file)
  const randomSuffix = Math.random().toString(36).slice(2, 10)
  const fileName = `${randomSuffix}_${Date.now()}.${ext}`
  const path = `workspaces/${workspaceId}/ops_compliance/${itemId}/${uid}/${fileName}`

  const storageRef = ref(firebaseStorage, path)
  await uploadBytes(storageRef, file, {
    contentType: file.type,
  })

  const downloadUrl = await getDownloadURL(storageRef)
  return { path, downloadUrl }
}

export async function getComplianceEvidenceUrl(path: string): Promise<string> {
  if (!path) return ''
  const storageRef = ref(firebaseStorage, path)
  return getDownloadURL(storageRef)
}
